<script lang="ts">
  import { form, bindClass } from "svelte-forms";
  import PhoneInput from "@shared/svelte/Components/PhoneInput.svelte";
  import PhoneMask from "@shared/svelte/Components/PhoneMask.svelte";
  import Autocomplete from "@shared/svelte/Components/Autocomplete.svelte";
  import Flash from "@shared/services/flashv2.js";
  import {
    distributorDomains,
    isValidDomain,
  } from "@shared/services/admin_sales_rep_domain_validation";

  import type { DistributorAccount } from "@shared/types/Distributor";
  import type { CreateRetailer, CreateRetailerUser } from "@shared/types/Retailer";
  import type { SalesRepToAdd, SalesRep } from "@shared/types/SalesRep";

  export let account: DistributorAccount;
  export let submittingData: boolean = false;
  export let runOnboarding: (
    retailerUser,
    retailer,
    selectedReps,
    accountNumber,
  ) => void = () => {};
  export let closeModal: () => void = () => {};
  export let accountNumberRequired: boolean = true;
  export let retailerUserRequired: boolean = false;
  export let validateDistributorDomain: boolean = true;
  export let salesRepApi = {
    url: "/api/admin/sales_reps/",
    params: () => ({ distributor_id: account.distributor.id, page: 1 }),
  } as { url: string; params: any };

  let distributorEmailDomainMatches: any[] = [];
  let selectedReps: SalesRep[] = [];
  let autocompleteSalesRep: SalesRep;
  let retailerUser: CreateRetailerUser = {};
  let retailer: CreateRetailer = {};
  let repToAdd: SalesRepToAdd;
  let invalidDomainError: boolean = false;

  const orderFrequency: any[] = window["C"].orderFrequency || [];
  const daysOfWeek: any[] = window["C"].daysOfWeek || [];
  const retailerLicenseTypes: any[] = window["C"].retailerLicenseTypes || [];

  function prefillRetailerFromAccount() {
    retailer.license_type = account.off_premises == true ? "off_premise" : "on_premise";
    retailer.liquor_license = account.liquor_license;
    retailer.name = account.name;
    retailer.address1 = account.address1;
    retailer.city = account.city;
    retailer.state = account.state;
    retailer.zip = account.zip;
    account.sales_reps?.forEach(function (rep) {
      addRep(rep);
    });

    retailerUser = account?.retailer_user || {};
  }
  prefillRetailerFromAccount();

  function addNewRep() {
    repToAdd = {};
  }

  function cancelSelectRep() {
    repToAdd = null;
  }

  function removeSelectedRep(removeRep) {
    selectedReps = selectedReps.filter(rep => {
      return rep.id != removeRep.id;
    });
  }

  function addRep(rep) {
    selectedReps.push(rep);
    selectedReps = selectedReps;
    repToAdd = null;
  }

  function fillRepForm() {
    repToAdd = autocompleteSalesRep;
    autocompleteSalesRep = null;
  }

  function enteringUser() {
    return (
      retailerUserRequired ||
      !!(
        retailerUser.first_name ||
        retailerUser.last_name ||
        retailerUser.email ||
        retailerUser.phone
      )
    );
  }

  function salesRepItemTemplate(item) {
    return (
      `<div editable=${item.editable} class="tagify__dropdown__item ${
        item.class ? item.class : ""
      }" tabindex="0" role="option">` +
      `<div class="fizz-styles" style="display: inline;">` +
      `<div class="default-body--bold padding-bottom--smallest"> ` +
      `${item.customer_service ? '<span class="fizz-pill-green" >cs</span>' : ""}` +
      `${item.name || ""} </div>` +
      '<div class="default-body--regular padding-bottom--smallest">' +
      item.contact_email +
      "</div>" +
      "</div>" +
      "</div>"
    );
  }

  const retailerOnboardingForm = form(() => ({
    name: { value: retailer.name, validators: ["required"] },
    accountNumber: {
      value: account.account_number,
      validators: ["required"],
      enabled: accountNumberRequired,
    },
    licenseType: { value: retailer.license_type, validators: ["required"] },
    address1: { value: retailer.address1, validators: ["required"] },
    city: { value: retailer.city, validators: ["required"] },
    state: { value: retailer.state, validators: ["required"] },
    zip: { value: retailer.zip, validators: ["required"] },
    firstName: {
      value: retailerUser.first_name,
      validators: ["required"],
      enabled: enteringUser(),
    },
    lastName: {
      value: retailerUser.last_name,
      validators: ["required"],
      enabled: enteringUser(),
    },
    email: {
      value: retailerUser.email,
      validators: ["required"],
      enabled: enteringUser(),
    },
    phone: {
      value: retailerUser.phone,
      validators: ["required"],
      enabled: enteringUser(),
    },
  }));

  function clearAndCloseModal() {
    retailer = {};
    selectedReps = [];
    closeModal();
  }

  function runOnboardingWrapper() {
    if (selectedReps.length == 0 && !account.distributor?.no_sales_reps_needed) {
      Flash.now({
        type: "warning",
        header: "Reps Required",
        message: "Please add at least one rep.",
      });
      return;
    }
    return runOnboarding(retailer, retailerUser, selectedReps, account);
  }

  const domainValidation = e => {
    if (!validateDistributorDomain) return;
    if (e.target.value == "") return (invalidDomainError = false);
    if (!e.target.value.split("@")[1]) return (invalidDomainError = false);

    let domain = e.target.value.split("@")[1];
    invalidDomainError = !isValidDomain(
      domain,
      account.distributor.id,
      distributorEmailDomainMatches,
    );
  };

  if (validateDistributorDomain) {
    distributorDomains({ state: account.distributor.state }).then(
      res => (distributorEmailDomainMatches = res),
    );
  }

  $: if (autocompleteSalesRep) {
    fillRepForm();
  }

  $: if (retailer.non_alcoholic) {
    retailer.liquor_license = null;
  }
</script>

<style>
  .cart__item-header {
    border-bottom: 2px solid #f5f7f7;
  }
  :global(input.invalid) {
    background-color: rgba(255, 0, 0, 0.3);
  }
</style>

<form on:submit|preventDefault={runOnboardingWrapper}>
  <div class="refer-and-earn-wrapper background-white position-relative">
    <div class="cart__item-header padding--medium">
      <div class="refer-and-earn-form">
        <div class="grid-row">
          <div class="grid-col c12-12">
            <div class="s3">Retailer Account Info</div>
          </div>
          <div class="grid-col c12-12--md c12-12">
            <div class="label input__label--left margin-top--small padding-bottom--smallest">
              Is this a Non-alcoholic Retailer?
            </div>
            <label>
              <input type="checkbox" bind:checked={retailer.non_alcoholic} />
              Yes
            </label>
          </div>
          <div class="grid-col c6-12--md c12-12">
            <div class="label input__label--left margin-top--small padding-bottom--smallest">
              Liquor License
            </div>
            <div class="input-wrapper full-width">
              <input
                type="text"
                class="input"
                bind:value={retailer.liquor_license}
                disabled={submittingData || retailer.non_alcoholic}
                use:bindClass={{ form: retailerOnboardingForm }}
                id="liquor-license-input"
              />
            </div>
          </div>
          <div class="grid-col c6-12--sm c12-12">
            <div class="label input__label--left margin-top--small padding-bottom--smallest">
              Liquor License Type*
            </div>
            <div class="input-wrapper select-style full-width">
              <select
                id="retailer_license_type"
                bind:value={retailer.license_type}
                use:bindClass={{ form: retailerOnboardingForm }}
              >
                {#each retailerLicenseTypes as licenseType}
                  <option value={licenseType.value}>{licenseType.label}</option>
                {/each}
              </select>
            </div>
          </div>
        </div>
        <div class="grid-row">
          <div class="grid-col c12-12">
            <div class="label input__label--left margin-top--small padding-bottom--smallest">
              Business Name*
            </div>
            <div class="input-wrapper full-width">
              <input
                type="text"
                class="input"
                bind:value={retailer.name}
                disabled={submittingData}
                use:bindClass={{ form: retailerOnboardingForm }}
                id="retailer-name-input"
              />
            </div>
          </div>
        </div>
        <div class="grid-row">
          <div class="grid-col c6-12--md c12-12">
            <div class="label input__label--left margin-top--small padding-bottom--smallest">
              Address*
            </div>
            <div class="input-wrapper full-width">
              <input
                type="text"
                class="input"
                name="address1"
                bind:value={retailer.address1}
                disabled={submittingData}
                use:bindClass={{ form: retailerOnboardingForm }}
                id="address1-input"
              />
            </div>
          </div>
          <div class="grid-col c6-12--md c12-12">
            <div class="label input__label--left margin-top--small padding-bottom--smallest">
              Address 2
            </div>
            <div class="input-wrapper full-width">
              <input
                type="text"
                class="input"
                bind:value={retailer.address2}
                disabled={submittingData}
                use:bindClass={{ form: retailerOnboardingForm }}
                id="address2-input"
              />
            </div>
          </div>
        </div>
        <div class="grid-row">
          <div class="grid-col c6-12--md c12-12">
            <div class="label input__label--left margin-top--small padding-bottom--smallest">
              City*
            </div>
            <div class="input-wrapper full-width">
              <input
                type="text"
                class="input"
                name="city"
                bind:value={retailer.city}
                disabled={submittingData}
                use:bindClass={{ form: retailerOnboardingForm }}
                id="city-input"
              />
            </div>
          </div>
          <div class="grid-col c6-12--md c12-12">
            <div class="label input__label--left margin-top--small padding-bottom--smallest">
              State*
            </div>
            <div class="input-wrapper full-width">
              <input
                type="text"
                class="input"
                name="state"
                bind:value={retailer.state}
                use:bindClass={{ form: retailerOnboardingForm }}
                id="state-input"
              />
            </div>
          </div>
        </div>
        <div class="grid-row">
          <div class="grid-col c6-12--md c12-12">
            <div class="label input__label--left margin-top--small padding-bottom--smallest">
              Zipcode*
            </div>
            <div class="input-wrapper full-width">
              <input
                type="text"
                class="input"
                name="zip"
                bind:value={retailer.zip}
                disabled={submittingData}
                use:bindClass={{ form: retailerOnboardingForm }}
                id="zipcode-input"
              />
            </div>
          </div>
        </div>
        <div class="grid-row">
          <div class="grid-col c6-12--md c12-12">
            <div class="label input__label--left margin-top--small padding-bottom--smallest">
              Order Day
            </div>
            <div class="input-wrapper select-style full-width">
              <select bind:value={retailer.order_day} id="select_order_day">
                {#each daysOfWeek as day}
                  <option value={day}>{day}</option>
                {/each}
              </select>
            </div>
          </div>
          <div class="grid-col c6-12--md c12-12">
            <div class="label input__label--left margin-top--small padding-bottom--smallest">
              Order Frequency
            </div>
            <div class="input-wrapper select-style full-width">
              <select bind:value={retailer.order_frequency} id="select_order_frequency">
                {#each orderFrequency as freq}
                  <option value={freq.key}>{freq.value}</option>
                {/each}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="refer-and-earn-wrapper background-white position-relative">
    <div class="cart__item-header padding--medium">
      <div class="refer-and-earn-form">
        <div class="grid-row">
          <div class="grid-col c12-12">
            <div class="s3">Retailer User{retailerUserRequired ? "" : " (optional)"}:</div>
          </div>
        </div>
        <div class="grid-row">
          <div class="grid-col c6-12--md c12-12">
            <div class="label input__label--left margin-top--small padding-bottom--smallest">
              First Name{retailerUserRequired ? "*" : ""}
            </div>
            <div class="input-wrapper full-width">
              <input
                type="text"
                class="input"
                name="firstName"
                bind:value={retailerUser.first_name}
                disabled={submittingData}
                use:bindClass={{ form: retailerOnboardingForm }}
                id="first-name-input"
              />
            </div>
          </div>
          <div class="grid-col c6-12--md c12-12">
            <div class="label input__label--left margin-top--small padding-bottom--smallest">
              Last Name{retailerUserRequired ? "*" : ""}
            </div>
            <div class="input-wrapper full-width">
              <input
                type="text"
                class="input"
                name="lastName"
                bind:value={retailerUser.last_name}
                disabled={submittingData}
                use:bindClass={{ form: retailerOnboardingForm }}
                id="last-name-input"
              />
            </div>
          </div>
        </div>
        <div class="grid-row padding-bottom--smaller">
          <div class="grid-col c6-12--md c12-12">
            <div class="label input__label--left margin-top--small padding-bottom--smallest">
              Email{retailerUserRequired ? "*" : ""}
            </div>
            <div class="input-wrapper full-width">
              <input
                type="text"
                class="input"
                name="email"
                bind:value={retailerUser.email}
                disabled={submittingData}
                use:bindClass={{ form: retailerOnboardingForm }}
                id="email-input"
              />
            </div>
          </div>
          <div class="grid-col c6-12--md c12-12">
            <div class="label input__label--left margin-top--small padding-bottom--smallest">
              Phone{retailerUserRequired ? "*" : ""}
            </div>
            <div class="input-wrapper full-width">
              <PhoneInput
                bind:value={retailerUser.phone}
                placeholder="Phone"
                extraClasses="input phone-input"
                disabled={submittingData}
                id="phone-input"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="refer-and-earn-wrapper background-white position-relative">
    <div class="cart__item-header padding--medium">
      <div class="refer-and-earn-form">
        <div class="grid-row">
          <div class="grid-col c12-12">
            <div class="s3 padding-bottom--small">Sales Reps for this account</div>
          </div>
        </div>
        {#if repToAdd}
          <div>
            <div class="grid-col c12-12">
              <div
                class="grid-row padding-left--smaller padding-right--smaller padding-bottom--small"
              >
                <div class="label margin-bottom--smallest">Search Sales Reps</div>
                <div
                  class="sales-rep-autocomplete default-body--regular"
                  data-testid="sales-rep-autocomplete"
                >
                  <Autocomplete
                    placeholder="Search your sales reps"
                    objectType="sales_rep"
                    searchUrlInput={salesRepApi.url}
                    dropdownItemTemplateCB={salesRepItemTemplate}
                    apiParams={salesRepApi.params(autocompleteSalesRep)}
                    bind:value={autocompleteSalesRep}
                  />
                </div>
              </div>
              <div class="grid-row">
                <div class="grid-col c12-12 c4-12--md margin-bottom--small">
                  <div class="label input__label--left padding-bottom--smallest">Rep Name</div>
                  <div class="input-wrapper full-width">
                    <input
                      type="text"
                      class="input"
                      bind:value={repToAdd.name}
                      disabled={submittingData}
                    />
                  </div>
                </div>
                <div class="grid-col c12-12 c4-12--md margin-bottom--small">
                  <div class="label input__label--left padding-bottom--smallest">
                    Rep Email Address
                  </div>
                  <div class="input-wrapper full-width">
                    <input
                      type="text"
                      class="input"
                      bind:value={repToAdd.contact_email}
                      disabled={submittingData}
                      on:keyup={domainValidation}
                      on:blur={domainValidation}
                    />
                  </div>
                </div>
                <div class="grid-col c12-12 c4-12--md margin-bottom--small">
                  <div class="label input__label--left padding-bottom--smallest">Rep Phone</div>
                  <div class="input-wrapper full-width">
                    <PhoneInput
                      bind:value={repToAdd.contact_phone}
                      placeholder="Phone"
                      extraClasses="input phone-input"
                      disabled={submittingData}
                    />
                  </div>
                </div>
              </div>
              {#if invalidDomainError}
                <div class="grid-row fizz-styles">
                  <div class="grid-col c12-12 fizz-text-urgent fizz-stack-16">
                    This email belongs to a different distributor. Select a different distributor or
                    enter a new email.
                  </div>
                </div>
              {/if}
              <div class="grid-row">
                <div class="align-left padding-left--smaller padding-right--smaller">
                  <button
                    class="button button--primary select-rep"
                    style="font: caption !important; margin-right: 10px;"
                    disabled={submittingData ||
                      invalidDomainError ||
                      !((repToAdd.contact_email || repToAdd.contact_phone) && repToAdd.name)}
                    on:click={() => addRep(repToAdd)}
                    type="button">Select Rep</button
                  >
                  <button
                    class="button button--secondary cancel-rep"
                    style="font: caption !important;"
                    on:click={cancelSelectRep}
                    type="button">Cancel</button
                  >
                </div>
              </div>
            </div>
          </div>
        {/if}
        <div class="grid-row padding-bottom--smaller">
          <div class="grid-col c12-12">
            {#each selectedReps as selectedRep}
              <div
                class="grid-col c12-12 align-left padding-top--smaller padding-bottom--smaller background-white margin-bottom--smaller"
                id="sales_rep_{selectedRep.id}"
              >
                <div class="grid-row padding-bottom--smaller">
                  <div class="grid-col c8-12">
                    <span class="default-body--bold default-subheader--bold--sm"
                      >{selectedRep.name}</span
                    >
                  </div>
                  <div class="grid-col c4-12 align-right">
                    {#if !selectedRep.integrated}
                      <button
                        on:click={() => removeSelectedRep(selectedRep)}
                        type="button"
                        class="button button--alert button--label">Remove Rep</button
                      >
                    {/if}
                  </div>
                </div>
                <div class="grid-row">
                  <hr class="hr hr-card-separator--header" />
                </div>
                <div class="grid-row padding-top--smaller">
                  <div class="grid-col c12-12">
                    {selectedRep.contact_email || "--"}
                  </div>
                  <div class="grid-col c6-12">
                    <PhoneMask input={selectedRep.contact_phone || "N/A"} />
                  </div>
                </div>
              </div>
            {/each}
            {#if !repToAdd}
              <button
                class="button button--primary cancel-rep margin-bottom--smaller"
                on:click={addNewRep}
                type="button">Add Rep</button
              >
            {/if}
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="refer-and-earn-wrapper background-white position-relative">
    <div class="cart__item-header padding--medium">
      <div class="refer-and-earn-form">
        <div class="grid-row padding-top--small">
          <div class="grid-col c12-12 align-center s4">
            <button
              class="button button--primary button--large full-width padding--small"
              disabled={submittingData || !$retailerOnboardingForm.valid}
              type="submit"
            >
              <span>Click to Submit</span>
            </button>
          </div>
        </div>
        <div class="grid-row padding-top--small margin-top--smallest">
          <div class="grid-col c12-12 align-center">
            <div class="color-grayDark s3">
              <a
                href={null}
                class="cursor-pointer"
                on:click={clearAndCloseModal}
                on:keypress={clearAndCloseModal}
                type="button">Cancel</a
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</form>
